import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { authApi } from '../config/apiClient';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  InputAdornment,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  Link,
  Divider,
  Modal,
  Card,
  CardContent
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import GoogleIcon from '@mui/icons-material/Google';
import { GOOGLE_LOGIN_ENABLED } from '../config/featureFlags';
import KvkkModal from './KvkkModal';
import { useTranslation } from 'react-i18next';
import Header from './welcome/components/Header';
import Footer from './welcome/components/Footer';
import { BRAND, BRAND_GRADIENTS } from '../config/brand';
import WhatsAppFloatingButton from './common/WhatsAppFloatingButton';

export interface RegisterPublicProps {
  embedded?: boolean;
  onRequestClose?: () => void;
  /** Gömülü modda geri → rol seçimine dönmek için */
  onEmbeddedBack?: () => void;
}

// Stil tanımlamaları
const PageContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: BRAND_GRADIENTS.vertical, // BRAND'den
  position: 'relative',
  overflowY: 'scroll',
}));

const RegisterContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4, 2),
  [theme.breakpoints.down('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(2, 1),
  },
}));

const RegisterSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: '1300px',
  margin: '0 auto',
  gap: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const Image = styled('img')(({ theme }) => ({
  maxWidth: '90%',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.03)',
  },
}));


const FormContainer = styled(Box)(({ theme }) => ({
  flex: 1.2,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1,
  maxWidth: '450px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const RegisterForm = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 16,
  maxWidth: 450,
  width: '100%',
  margin: '0 auto',
  position: 'relative',
}));

const Logo = styled('img')({
  width: 220,
  maxWidth: '100%',
  height: 'auto',
  borderRadius: 10,
  marginBottom: 12,
  objectFit: 'contain',
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
  // Genel yapıyı bozmadan input metinlerini bir tık büyüt
  '& .MuiInputLabel-root': {
    fontSize: '1.05rem',
  },
  '& .MuiInputBase-input': {
    fontSize: '1.1rem',
  },
}));

const RegisterButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: 12,
  fontWeight: 'bold',
  fontSize: 16,
  width: '100%',
  backgroundColor: '#0D47A1',
  '&:hover': {
    backgroundColor: '#0A3C87',
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  left: 16,
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  backgroundColor: 'rgba(13, 71, 161, 0.1)',
  color: '#0D47A1',
  '&:hover': {
    backgroundColor: 'rgba(13, 71, 161, 0.2)',
  },
}));

const CompanyModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  maxWidth: 450,
  padding: 0,
  outline: 'none',
  borderRadius: 16,
  overflow: 'hidden',
}));

const Register: React.FC<RegisterPublicProps> = ({
  embedded = false,
  onRequestClose,
  onEmbeddedBack,
}) => {
  const normalizePhoneInput = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const withLeadingZero = digits.startsWith('0') ? digits : `0${digits}`;
    return withLeadingZero.slice(0, 11);
  };

  const { t } = useTranslation();
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('0');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [isKvkkModalOpen, setIsKvkkModalOpen] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  // Google için firma adı modalı state'leri
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [googleCompanyName, setGoogleCompanyName] = useState('');
  
  // Apple için firma adı modalı state'leri
  const [showAppleCompanyModal, setShowAppleCompanyModal] = useState(false);
  const [appleCompanyName, setAppleCompanyName] = useState('');
  const [appleLoading, setAppleLoading] = useState(false);
  
  const navigate = useNavigate();

  const goToLoginAfterSuccess = (credentials?: { email: string; password: string }) => {
    if (embedded) {
      onRequestClose?.();
    }
    if (credentials?.email && credentials?.password) {
      navigate('/login', {
        state: {
          autoLogin: true,
          credentials,
        },
      });
      return;
    }
    navigate('/login');
  };

  const handleBackNavigation = () => {
    if (embedded) {
      (onEmbeddedBack ?? onRequestClose)?.();
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/login');
  };

  // Google OAuth Client ID
  const GOOGLE_CLIENT_ID = "338071169045-4kj277asrv67kp1u8mtn84p4gvo3pubh.apps.googleusercontent.com";

  // Google OAuth script yükle
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Apple Sign In script yükle
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const normalizedPhone = normalizePhoneInput(phone);
    
    // Form doğrulama
    if (!companyName) {
      setError(t('registerPage.formErrors.companyNameRequired'));
      return;
    }

    if (!firstName) {
      setError(t('registerPage.formErrors.firstNameRequired'));
      return;
    }

    if (!lastName) {
      setError(t('registerPage.formErrors.lastNameRequired'));
      return;
    }

    if (!normalizedPhone || normalizedPhone === '0') {
      setError(t('registerPage.formErrors.phoneRequired'));
      return;
    }
    if (!/^0\d{10}$/.test(normalizedPhone)) {
      setError(t('registerPage.formErrors.invalidPhone'));
      return;
    }
    
    if (!email) {
      setError(t('registerPage.formErrors.emailRequired'));
      return;
    }
    
    if (!email.includes('@')) {
      setError(t('registerPage.formErrors.invalidEmail'));
      return;
    }
    
    if (!password) {
      setError(t('registerPage.formErrors.passwordRequired'));
      return;
    }

    if (password.length < 6) {
      setError(t('registerPage.formErrors.passwordLength'));
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t('registerPage.formErrors.passwordMismatch'));
      return;
    }
    
    if (!kvkkAccepted) {
      setError(t('registerPage.formErrors.kvkkRequired'));
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      console.log('🚀 Kayıt işlemi başlıyor...');
      console.log('📦 Request Data:', { companyName, firstName, lastName, phone, email, password: '***' });

      // Backend API'ye kayıt isteği gönderme
      const response = await authApi.post(`/api/auth/register?t=${Date.now()}`, {
        companyName,
        firstName,
        lastName,
        phone: normalizedPhone,
        email,
        password
      });

      console.log('Kayıt başarılı!', response.data);
      
      setSuccess(t('registerPage.registrationSuccess'));
      
      setTimeout(() => {
        goToLoginAfterSuccess({
          email: email.trim(),
          password,
        });
      }, 2000);
      
    } catch (err: any) {
      console.error('❌ Hata oluştu:', err);
      console.error('❌ Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          headers: err.config?.headers
        }
      });
      
      let errorMessage = t('registerPage.registrationError');
      
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKvkkModalOpen = () => setIsKvkkModalOpen(true);
  const handleKvkkModalClose = () => setIsKvkkModalOpen(false);

  // Google ile kayıt - önce firma adı modal aç
  const handleGoogleRegisterClick = () => {
    setShowCompanyModal(true);
    setGoogleCompanyName('');
    setError('');
  };

  // Firma adı modalını kapat
  const handleCloseCompanyModal = () => {
    setShowCompanyModal(false);
    setGoogleCompanyName('');
    setGoogleLoading(false);
  };

  // Google OAuth'u başlat (firma adı girildikten sonra)
  const handleGoogleOAuthStart = () => {
    if (!googleCompanyName.trim()) {
      setError('Lütfen firma adını girin.');
      return;
    }

    if (!window.google || !window.google.accounts) {
      setError('Google servisi yüklenemedi. Lütfen sayfayı yenileyin.');
      return;
    }

    setGoogleLoading(true);
    setError('');
    setShowCompanyModal(false);

    // Google OAuth token client oluştur
    const client = (window.google.accounts as any).oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'email profile',
      callback: async (response: any) => {
        if (response.access_token) {
          try {
            // Google'dan kullanıcı bilgilerini al
            const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
              headers: {
                Authorization: `Bearer ${response.access_token}`,
              },
            });

            if (!userInfoResponse.ok) {
              throw new Error('Google kullanıcı bilgileri alınamadı');
            }

            const userInfo = await userInfoResponse.json();
            console.log('Google User Info:', userInfo);

            // Backend'e Google ile firma kaydı isteği gönder
            const registerResponse = await authApi.post('/api/auth/register-google', {
              googleToken: response.access_token,
              companyName: googleCompanyName.trim(),
              email: userInfo.email,
              firstName: userInfo.given_name || '',
              lastName: userInfo.family_name || '',
              photoURL: userInfo.picture || null,
              googleId: userInfo.sub
            });

            console.log('Google ile firma kaydı başarılı!', registerResponse.data);
            setSuccess(registerResponse.data.message || 'Google ile kayıt başarılı! Giriş sayfasına yönlendiriliyorsunuz...');

            setTimeout(() => {
              goToLoginAfterSuccess();
            }, 2000);

          } catch (err: any) {
            console.error('Google ile kayıt hatası:', err);
            let errorMessage = 'Google ile kayıt sırasında bir hata oluştu.';
            if (err.response?.data?.message) {
              errorMessage = err.response.data.message;
            } else if (err.message) {
              errorMessage = err.message;
            }
            setError(errorMessage);
            setGoogleLoading(false);
          }
        } else {
          setError('Google ile giriş iptal edildi.');
          setGoogleLoading(false);
        }
      },
      error_callback: (error: any) => {
        console.error('Google OAuth hatası:', error);
        setError('Google ile giriş sırasında bir hata oluştu.');
        setGoogleLoading(false);
      },
    });

    // Token isteğini başlat
    client.requestAccessToken();
  };

  // Apple ile kayıt - önce firma adı modal aç
  const handleAppleRegisterClick = () => {
    setShowAppleCompanyModal(true);
    setAppleCompanyName('');
    setError('');
  };

  // Apple firma adı modalını kapat
  const handleCloseAppleCompanyModal = () => {
    setShowAppleCompanyModal(false);
    setAppleCompanyName('');
    setAppleLoading(false);
  };

  // Apple Sign In'i başlat (firma adı girildikten sonra)
  const handleAppleOAuthStart = async () => {
    if (!appleCompanyName.trim()) {
      setError('Lütfen firma adını girin.');
      return;
    }

    if (!(window as any).AppleID) {
      setError('Apple servisi yüklenemedi. Lütfen sayfayı yenileyin.');
      return;
    }

    setAppleLoading(true);
    setError('');
    setShowAppleCompanyModal(false);

    try {
      // Apple Sign In yapılandırması
      (window as any).AppleID.auth.init({
        clientId: 'com.mt-teknoloji.gts.signin', // Services ID
        scope: 'name email',
        redirectURI: BRAND.apps.appleRedirectUri, // Apple Developer'da kayıtlı tam URL
        usePopup: true
      });

      // Apple Sign In popup'ını aç
      const response = await (window as any).AppleID.auth.signIn();
      console.log('Apple Sign In response:', response);

      // Backend'e Apple ile firma kaydı isteği gönder (Google gibi)
      const registerResponse = await authApi.post('/api/auth/register-apple', {
        appleToken: response.authorization.id_token,
        companyName: appleCompanyName.trim(),
        appleUser: response.user || null, // İlk girişte kullanıcı bilgileri gelir
        authorizationCode: response.authorization.code
      });

      console.log('Apple ile firma kaydı başarılı!', registerResponse.data);
      setSuccess(registerResponse.data.message || 'Apple ile kayıt başarılı! Giriş sayfasına yönlendiriliyorsunuz...');

      setTimeout(() => {
        goToLoginAfterSuccess();
      }, 2000);

    } catch (err: any) {
      console.error('Apple ile kayıt hatası:', err);
      let errorMessage = 'Apple ile kayıt sırasında bir hata oluştu.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setAppleLoading(false);
    }
  };

  const formTree = (
    <>
            <RegisterForm elevation={embedded ? 0 : 3} sx={embedded ? { boxShadow: 'none', maxWidth: '100%' } : undefined}>
          <BackButton 
            startIcon={<KeyboardBackspaceIcon />}
            onClick={handleBackNavigation}
            size="small"
          >
            {t('registerPage.backButton')}
          </BackButton>
          
          <Logo src={BRAND.logos.loginForm} alt={`${BRAND.name} Logo`} />
          
          <Typography 
            variant="h5" 
            component="h1" 
            gutterBottom 
            sx={{ fontWeight: 'bold', textAlign: 'center', mb: 0.5, fontSize: { xs: '1.5rem', sm: '1.65rem' } }}
          >
            {t('registerPage.title')}
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{
              textAlign: 'center',
              mb: 2,
              color: 'text.secondary',
              fontSize: { xs: '1.0rem', sm: '1.05rem' },
              lineHeight: 1.35,
            }}
          >
            {t('registerPage.subtitle')}
          </Typography>
          
          <Box component="form" onSubmit={handleSubmit} width="100%">
            {/* Firma Adı */}
            <StyledTextField
              variant="outlined"
              fullWidth
              id="companyName"
              label={t('registerPage.companyNameLabel')}
              name="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Ad */}
            <StyledTextField
              variant="outlined"
              fullWidth
              id="firstName"
              label={t('registerPage.firstNameLabel')}
              name="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Soyad */}
            <StyledTextField
              variant="outlined"
              fullWidth
              id="lastName"
              label={t('registerPage.lastNameLabel')}
              name="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Telefon */}
            <StyledTextField
              variant="outlined"
              fullWidth
              id="phone"
              label={t('registerPage.phoneLabel')}
              name="phone"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(normalizePhoneInput(e.target.value))}
              required
              size="small"
              inputProps={{ maxLength: 11 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* E-posta */}
            <StyledTextField
              variant="outlined"
              fullWidth
              id="email"
              label={t('registerPage.emailLabel')}
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Şifre */}
            <StyledTextField
              variant="outlined"
              fullWidth
              name="password"
              label={t('registerPage.passwordLabel')}
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Şifre Tekrar */}
            <StyledTextField
              variant="outlined"
              fullWidth
              name="confirmPassword"
              label={t('registerPage.confirmPasswordLabel')}
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* KVKK Onayı */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={kvkkAccepted}
                  onChange={(e) => setKvkkAccepted(e.target.checked)}
                  name="kvkkAccepted"
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.88rem' }}>
                  <Link 
                    href="#" 
                    onClick={(e) => { 
                      e.preventDefault(); 
                      handleKvkkModalOpen();
                    }}
                    underline="hover"
                  >
                    {t('registerPage.kvkkLink')}
                  </Link>
                  {' '}{t('registerPage.kvkkAgreement')}
                </Typography>
              }
              sx={{ mb: 1, mt: 1, alignItems: 'flex-start' }}
            />
            
            {/* Kayıt Butonları - 3'lü Grid */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {/* Kayıt Ol Butonu */}
              <Button
                type="submit"
                variant="outlined"
                disabled={loading || !kvkkAccepted}
                size="medium"
                sx={{ 
                  flex: 1, 
                  py: 1.5,
                  minWidth: 'auto',
                  borderRadius: 3,
                  fontWeight: 'bold',
                  fontSize: 16,
                  borderColor: '#dadce0',
                  color: '#0D47A1',
                  '&:hover': {
                    borderColor: '#0D47A1',
                    backgroundColor: 'rgba(13, 71, 161, 0.04)',
                  }
                }}
              >
                {loading ? <CircularProgress size={20} style={{ color: '#0D47A1' }} /> : t('registerPage.registerButton')}
              </Button>

              {/* Google İkonu Butonu - geçici olarak gizli (bkz. config/featureFlags.ts) */}
              {GOOGLE_LOGIN_ENABLED && (
              <Button
                variant="outlined"
                disabled={googleLoading || loading}
                onClick={handleGoogleRegisterClick}
                sx={{ 
                  flex: 1, 
                  py: 1.5,
                  minWidth: 'auto',
                  borderRadius: 3,
                  borderColor: '#dadce0',
                  '&:hover': {
                    borderColor: '#4285F4',
                    backgroundColor: 'rgba(66, 133, 244, 0.04)',
                  }
                }}
              >
                {googleLoading ? (
                  <CircularProgress size={24} style={{ color: '#4285F4' }} />
                ) : (
                  <GoogleIcon style={{ color: '#4285F4', fontSize: 32 }} />
                )}
              </Button>
              )}

              {/* Apple İkonu Butonu */}
              <Button
                variant="outlined"
                disabled={appleLoading || loading}
                onClick={handleAppleRegisterClick}
                sx={{ 
                  flex: 1, 
                  py: 1.5,
                  minWidth: 'auto',
                  borderRadius: 3,
                  borderColor: '#dadce0',
                  '&:hover': {
                    borderColor: '#000',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                {appleLoading ? (
                  <CircularProgress size={24} style={{ color: '#000' }} />
                ) : (
                  <svg width="28" height="28" viewBox="0 0 814 1000" fill="#000000">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
                  </svg>
                )}
              </Button>
            </Box>
          </Box>
          
          <Snackbar 
            open={!!error} 
            autoHideDuration={6000} 
            onClose={() => setError('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setError('')} severity="error">
              {error}
            </Alert>
          </Snackbar>
          
          <Snackbar 
            open={!!success} 
            autoHideDuration={6000} 
            onClose={() => setSuccess('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccess('')} severity="success">
              {success}
            </Alert>
          </Snackbar>
        </RegisterForm>
        
        {/* KVKK Modalı */}
        <KvkkModal open={isKvkkModalOpen} onClose={handleKvkkModalClose} />

        {/* Google için Firma Adı Modalı */}
        <Modal
          open={showCompanyModal}
          onClose={handleCloseCompanyModal}
          aria-labelledby="company-name-modal"
        >
          <CompanyModalContainer>
            <Card elevation={5}>
              <Box sx={{ 
                py: 3, 
                px: 3, 
                textAlign: 'center',
                background: 'linear-gradient(to right, #0D47A1, #1976D2)',
                color: 'white'
              }}>
                <Typography 
                  id="company-name-modal" 
                  variant="h5" 
                  component="h2"
                  fontWeight="bold"
                >
                  Firma Bilgisi
                </Typography>
              </Box>
              
              <CardContent sx={{ p: 4 }}>
                <Typography 
                  variant="body1" 
                  align="center" 
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Google ile kayıt olmak için lütfen firma adınızı girin.
                </Typography>
                
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  id="googleCompanyName"
                  label="Firma Adı"
                  name="googleCompanyName"
                  value={googleCompanyName}
                  onChange={(e) => setGoogleCompanyName(e.target.value)}
                  required
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                  <Button 
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleGoogleOAuthStart}
                    disabled={googleLoading || !googleCompanyName.trim()}
                    sx={{ 
                      py: 1.5, 
                      fontSize: '1rem', 
                      borderRadius: '12px',
                      backgroundColor: '#0D47A1',
                      '&:hover': {
                        backgroundColor: '#0A3C87',
                      },
                    }}
                    startIcon={<GoogleIcon />}
                  >
                    {googleLoading ? <CircularProgress size={24} color="inherit" /> : 'Google ile Devam Et'}
                  </Button>
                  
                  <Button 
                    onClick={handleCloseCompanyModal}
                    sx={{ color: 'text.secondary' }}
                    fullWidth
                  >
                    İptal
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </CompanyModalContainer>
        </Modal>

        {/* Apple için Firma Adı Modalı */}
        <Modal
          open={showAppleCompanyModal}
          onClose={handleCloseAppleCompanyModal}
          aria-labelledby="apple-company-name-modal"
        >
          <CompanyModalContainer>
            <Card elevation={5}>
              <Box sx={{ 
                py: 3, 
                px: 3, 
                textAlign: 'center',
                background: 'linear-gradient(to right, #000000, #333333)',
                color: 'white'
              }}>
                <Typography 
                  id="apple-company-name-modal" 
                  variant="h5" 
                  component="h2"
                  fontWeight="bold"
                >
                  Firma Bilgisi
                </Typography>
              </Box>
              
              <CardContent sx={{ p: 4 }}>
                <Typography 
                  variant="body1" 
                  align="center" 
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Apple ile kayıt olmak için lütfen firma adınızı girin.
                </Typography>
                
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  id="appleCompanyName"
                  label="Firma Adı"
                  name="appleCompanyName"
                  value={appleCompanyName}
                  onChange={(e) => setAppleCompanyName(e.target.value)}
                  required
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                  <Button 
                    variant="contained"
                    fullWidth
                    onClick={handleAppleOAuthStart}
                    disabled={appleLoading || !appleCompanyName.trim()}
                    sx={{ 
                      py: 1.5, 
                      fontSize: '1rem', 
                      borderRadius: '12px',
                      backgroundColor: '#000000',
                      '&:hover': {
                        backgroundColor: '#333333',
                      },
                    }}
                    startIcon={
                      <svg width="20" height="20" viewBox="0 0 814 1000" fill="#FFFFFF">
                        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
                      </svg>
                    }
                  >
                    {appleLoading ? <CircularProgress size={24} color="inherit" /> : 'Apple ile Devam Et'}
                  </Button>
                  
                  <Button 
                    onClick={handleCloseAppleCompanyModal}
                    sx={{ color: 'text.secondary' }}
                    fullWidth
                  >
                    İptal
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </CompanyModalContainer>
        </Modal>
    </>
  );

  if (embedded) {
    return <Box sx={{ width: '100%' }}>{formTree}</Box>;
  }

  return (
    <PageContainer>
      <Header />
      <RegisterContent>
        <RegisterSection>
          <FormContainer>
            {formTree}
          </FormContainer>
          
          <ImageContainer>
            <Image 
              src="/assets/sirketgiris.png" 
              alt="Şirket Kayıt" 
            />
          </ImageContainer>
        </RegisterSection>
      </RegisterContent>
      <Footer />
      <WhatsAppFloatingButton />
    </PageContainer>
  );
};

export default Register; 